import React from 'react'
import { compose, withProps } from 'recompose'
import classNames from 'classnames'
import { range } from 'ramda'
import { Link } from 'react-router-dom'
import Grid from '@material-ui/core/Grid'
import { withStyles } from '@material-ui/core/styles'

import Navigation from '../layouts/Navigation'

import Heading2 from '../styleguide/Heading2'
import Paragraph from '../styleguide/Paragraph'

import { ArrowRight, ArrowLeft } from '../components/Icons'
import NewsItem from '../components/NewsItem'

import withNews from '../hocs/withNews'

const NEWS_PER_PAGE = 10

const styles = theme => ({
  containerStyle: {
    flex: '1 0 auto',
    padding: '0 36px 100px 36px',
    background: '#fafafa'
  },
  gridStyle: {
    maxWidth: 1200,
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  contentContainerStyle: {
    marginTop: 70,
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  headingStyle: {
    marginBottom: 20
  },
  paginationContainerStyle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
    marginTop: 30
  },
  paginationBaseLinkStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'none',
    backgroundColor: '#fff',
    color: '#303a44',
    border: '1px solid #d6d8db',
    minWidth: 40,
    width: 40,
    height: 40,
    padding: 10,
    marginLeft: -1,
    '&:hover, &:focus': {
      zIndex: 2,
      backgroundColor: '#e2e3e4'
    }
  },
  paginationActiveLinkStyle: {
    backgroundColor: '#303c49',
    borderColor: '#303c49',
    zIndex: 3,
    color: '#d6d8db',
    '&:hover, &:focus, &:active': {
      backgroundColor: '#303c49',
      borderColor: '#303c49',
      zIndex: 3,
      color: '#d6d8db',
      cursor: 'default'
    }
  },
  paginationLeftLinkStyle: {
    marginLeft: 0,
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    color: '#e40045'
  },
  paginationRightLinkStyle: {
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
    color: '#e40045'
  },
  labelColorStyle: {
    color: 'currentColor'
  }
})

const Pagination = ({
  indexPages,
  leftLink,
  rightLink,
  classes: {
    paginationContainerStyle,
    paginationBaseLinkStyle,
    paginationLeftLinkStyle,
    paginationRightLinkStyle,
    paginationActiveLinkStyle,
    labelColorStyle
  }
}) => {
  const indexPagesLinks = indexPages.map(({ pageLink, label, activeLink }) => (
    <Link
      to={pageLink}
      key={label}
      className={classNames(
        paginationBaseLinkStyle,
        activeLink && paginationActiveLinkStyle
      )}
    >
      <Paragraph className={labelColorStyle}>{label}</Paragraph>
    </Link>
  ))

  return (
    <div className={paginationContainerStyle}>
      {leftLink && (
        <Link
          to={leftLink}
          className={classNames(
            paginationBaseLinkStyle,
            paginationLeftLinkStyle
          )}
        >
          <ArrowLeft size={12} />
        </Link>
      )}
      {indexPagesLinks}
      {rightLink && (
        <Link
          to={rightLink}
          className={classNames(
            paginationBaseLinkStyle,
            paginationRightLinkStyle
          )}
        >
          <ArrowRight size={12} />
        </Link>
      )}
    </div>
  )
}

const NewsPagination = withStyles(styles)(Pagination)

class News extends React.Component {
  constructor() {
    super()
    this.initTimestamp = Date.now()
  }

  beforeUnloadHandler = () => {
    const { setNewsReadTimestamp } = this.props
    setNewsReadTimestamp(this.initTimestamp)
  }

  componentDidMount() {
    window.addEventListener('beforeunload', this.beforeUnloadHandler)
  }

  componentWillUnmount() {
    const { setNewsReadTimestamp } = this.props
    setNewsReadTimestamp(this.initTimestamp)
    window.removeEventListener('beforeunload', this.beforeUnloadHandler)
  }

  render() {
    const {
      totalNewsCount,
      news,
      newsPagination,

      classes: {
        containerStyle,
        gridStyle,
        contentContainerStyle,
        headingStyle
      }
    } = this.props

    return (
      <div className={containerStyle}>
        <Grid container spacing={0} className={gridStyle}>
          <Navigation enableOverviewShortcuts={false} />
          <Grid item xs={8} className={contentContainerStyle}>
            <Heading2 className={headingStyle}>News</Heading2>
            <div>
              {news.map(
                ({
                  id,
                  headline,
                  text,
                  publicationDate,
                  linkUrl,
                  linkLabel,
                  isUnread,
                  teaserImage
                }) => (
                  <NewsItem
                    key={id}
                    headline={headline}
                    text={text}
                    teaserImage={teaserImage}
                    date={publicationDate}
                    isUnread={isUnread}
                    linkUrl={linkUrl}
                    linkLabel={linkLabel}
                  />
                )
              )}
            </div>
            {totalNewsCount && totalNewsCount > NEWS_PER_PAGE && (
              <NewsPagination
                indexPages={newsPagination.indexPages}
                leftLink={newsPagination.leftLink}
                rightLink={newsPagination.rightLink}
              />
            )}
          </Grid>
        </Grid>
      </div>
    )
  }
}

export default compose(
  withProps(({ match: { params } }) => {
    const currentNewsPage = params.page ? parseInt(params.page, 10) : 0

    return {
      newsSize: NEWS_PER_PAGE,
      newsSkip: currentNewsPage * NEWS_PER_PAGE,
      currentNewsPage
    }
  }),
  withNews(),
  withProps(({ totalNewsCount, currentNewsPage }) => {
    const pageCount = Math.ceil(totalNewsCount / NEWS_PER_PAGE)

    const indexPages = range(0, pageCount).map(pageIndex => {
      const pageLink = pageIndex === 0 ? '/news' : `/news/${pageIndex}`
      return {
        pageLink,
        label: pageIndex + 1,
        activeLink: pageIndex === currentNewsPage
      }
    })

    const leftLink =
      currentNewsPage > 0
        ? currentNewsPage - 1 === 0
          ? '/news'
          : `/news/${currentNewsPage - 1}`
        : ''

    const rightLink =
      currentNewsPage === pageCount - 1 ? '' : `/news/${currentNewsPage + 1}`

    const newsPagination = {
      leftLink,
      indexPages,
      rightLink
    }

    return { newsPagination }
  }),
  withStyles(styles)
)(News)



// WEBPACK FOOTER //
// ./src/screens/News.js